/* ==========================================================
   ZEBAESH — TRACK ORDER
   SECURITY-FIRST FRONTEND CONTROLLER

   IMPORTANT:
   - NO PostEx key here.
   - NO Shopify Admin token here.
   - NO courier secret here.
   - Customer verification happens server-side later.
   ========================================================== */

(() => {
  'use strict';


  /* ========================================================
     CONFIG
     ======================================================== */

  const ZTRK_CONFIG = Object.freeze({

    /*
      Keep FALSE until secure backend + App Proxy
      + verification + rate limiting are ready.
    */
    live: false,

    /*
      Allows synthetic success data ONLY inside Shopify
      Theme Editor so full UI can be tested before API setup.
    */
    demoInThemeEditor: true,

    endpoint:
      '/apps/zebaesh-tracking/track',

    requestTimeout:
      15000,

    clientCooldown:
      1500,

    demoDelay:
      700

  });


  /* ========================================================
     SELECTORS
     ======================================================== */

  const SELECTORS = Object.freeze({

    root:
      '[data-ztrk-root]',

    form:
      '[data-ztrk-form]',

    orderInput:
      '[data-ztrk-order]',

    identityInput:
      '[data-ztrk-identity]',

    trackingInput:
      '[data-ztrk-tracking]',

    modeButton:
      '[data-ztrk-mode-button]',

    modePanel:
      '[data-ztrk-mode-panel]',

    submit:
      '[data-ztrk-submit]',

    reset:
      '[data-ztrk-reset]',

    state:
      '[data-ztrk-state]',

    announcement:
      '[data-ztrk-announcement]',

    demoBadge:
      '[data-ztrk-demo-badge]'

  });


  /* ========================================================
     BASIC HELPERS
     ======================================================== */

  function setText(element, value) {

    if (!element) return;

    element.textContent =
      value == null
        ? ''
        : String(value);

  }


  function wait(milliseconds) {

    return new Promise((resolve) => {
      window.setTimeout(
        resolve,
        milliseconds
      );
    });

  }


  function isThemeEditor() {

    return Boolean(
      window.Shopify &&
      window.Shopify.designMode
    );

  }


  function canUseDemo() {

    return (
      !ZTRK_CONFIG.live &&
      ZTRK_CONFIG.demoInThemeEditor &&
      isThemeEditor()
    );

  }


  /* ========================================================
     INPUT NORMALIZATION
     ======================================================== */

  function normalizeOrderNumber(value) {

    if (typeof value !== 'string') {
      return '';
    }

    return value
      .trim()
      .replace(/\s+/g, '')
      .slice(0, 60);

  }


  function normalizeIdentity(value) {

    if (typeof value !== 'string') {
      return '';
    }

    return value
      .trim()
      .slice(0, 160);

  }


  function normalizeTrackingNumber(value) {

    if (typeof value !== 'string') {
      return '';
    }

    return value
      .trim()
      .replace(/\s+/g, '')
      .slice(0, 80);

  }


  /* ========================================================
     CLIENT-SIDE UX VALIDATION

     Server-side validation will still be compulsory.
     ======================================================== */

  function validateOrderNumber(value) {

    return (
      value.length >= 2 &&
      value.length <= 60 &&
      /^[#A-Za-z0-9_-]+$/.test(value)
    );

  }


  function validateIdentity(value) {

    return (
      value.length >= 4 &&
      value.length <= 160
    );

  }


  function validateTrackingNumber(value) {

    return (
      value.length >= 4 &&
      value.length <= 80 &&
      /^[A-Za-z0-9_-]+$/.test(value)
    );

  }


  /* ========================================================
     ACCESSIBILITY
     ======================================================== */

  function announce(root, message) {

    const region =
      root.querySelector(
        SELECTORS.announcement
      );

    if (!region) return;

    setText(region, '');

    window.setTimeout(() => {
      setText(region, message);
    }, 20);

  }


  /* ========================================================
     TRACKER STATES
     ======================================================== */

  function setState(root, stateName) {

    const states =
      root.querySelectorAll(
        SELECTORS.state
      );

    states.forEach((state) => {

      const active =
        state.getAttribute(
          'data-ztrk-state'
        ) === stateName;

      state.classList.toggle(
        'is-active',
        active
      );

      state.hidden = !active;

      state.setAttribute(
        'aria-hidden',
        String(!active)
      );

    });

    root.dataset.currentState =
      stateName;

  }


  /* ========================================================
     FIELD ERRORS
     ======================================================== */

  function clearFieldError(input) {

    if (!input) return;

    input.removeAttribute(
      'aria-invalid'
    );

    const field =
      input.closest(
        '.ztrk-field'
      );

    if (!field) return;

    const error =
      field.querySelector(
        '[data-ztrk-error]'
      );

    if (error) {

      setText(error, '');

      error.hidden = true;

    }

  }


  function showFieldError(
    input,
    message
  ) {

    if (!input) return;

    input.setAttribute(
      'aria-invalid',
      'true'
    );

    const field =
      input.closest(
        '.ztrk-field'
      );

    if (!field) return;

    const error =
      field.querySelector(
        '[data-ztrk-error]'
      );

    if (error) {

      setText(
        error,
        message
      );

      error.hidden = false;

    }

  }


  /* ========================================================
     SUBMIT BUTTON
     ======================================================== */

  function setSubmitDefaultText(
    button,
    mode
  ) {

    if (!button) return;

    const text =
      mode === 'tracking'
        ? button.dataset.trackingText
        : button.dataset.orderText;

    if (!text) return;

    button.dataset.defaultText =
      text;

    const label =
      button.querySelector(
        '[data-ztrk-submit-label]'
      );

    setText(
      label,
      text
    );

  }


  function setSubmitting(
    button,
    submitting
  ) {

    if (!button) return;

    button.disabled =
      submitting;

    button.classList.toggle(
      'is-loading',
      submitting
    );

    button.setAttribute(
      'aria-busy',
      String(submitting)
    );


    const label =
      button.querySelector(
        '[data-ztrk-submit-label]'
      );


    if (submitting) {

      setText(
        label,
        button.dataset.loadingText ||
        'Checking Delivery'
      );

      return;

    }


    setText(
      label,
      button.dataset.defaultText ||
      'Track My Order'
    );

  }


  /* ========================================================
     LOOKUP MODE
     ======================================================== */

  function setLookupMode(
    root,
    mode
  ) {

    const validMode =
      mode === 'tracking'
        ? 'tracking'
        : 'order';


    root.dataset.lookupMode =
      validMode;


    const buttons =
      root.querySelectorAll(
        SELECTORS.modeButton
      );


    const panels =
      root.querySelectorAll(
        SELECTORS.modePanel
      );


    buttons.forEach((button) => {

      const active =
        button.dataset.ztrkModeButton ===
        validMode;

      button.classList.toggle(
        'is-active',
        active
      );

      button.setAttribute(
        'aria-selected',
        String(active)
      );

    });


    panels.forEach((panel) => {

      const active =
        panel.dataset.ztrkModePanel ===
        validMode;

      panel.classList.toggle(
        'is-active',
        active
      );

      panel.hidden =
        !active;


      panel
        .querySelectorAll('input')
        .forEach((input) => {

          input.disabled =
            !active;

          if (!active) {
            clearFieldError(input);
          }

        });

    });


    const submitButton =
      root.querySelector(
        SELECTORS.submit
      );


    setSubmitDefaultText(
      submitButton,
      validMode
    );


    announce(
      root,
      validMode === 'tracking'
        ? 'Tracking number lookup selected.'
        : 'Order number lookup selected.'
    );

  }


  /* ========================================================
     PROGRESS UI
     ======================================================== */

  function updateProgress(
    root,
    progressIndex
  ) {

    const current =
      Number.isFinite(
        Number(progressIndex)
      )
        ? Math.max(
            0,
            Math.min(
              4,
              Number(progressIndex)
            )
          )
        : 0;


    const steps =
      root.querySelectorAll(
        '[data-ztrk-progress-step]'
      );


    steps.forEach((step) => {

      const index =
        Number(
          step.dataset.ztrkProgressStep
        );


      step.classList.toggle(
        'is-complete',
        index < current
      );


      step.classList.toggle(
        'is-current',
        index === current
      );

    });

  }


  /* ========================================================
     RESULT RENDERER

     All API/customer-facing dynamic values use textContent.
     No raw courier HTML is injected.
     ======================================================== */

  function renderTrackingResult(
    root,
    data,
    mode
  ) {

    const referenceLabel =
      root.querySelector(
        '[data-ztrk-result-reference-label]'
      );


    const reference =
      root.querySelector(
        '[data-ztrk-result-reference]'
      );


    if (mode === 'tracking') {

      setText(
        referenceLabel,
        'TRACKING#'
      );

      setText(
        reference,
        data.tracking_number ||
        '—'
      );

    } else {

      setText(
        referenceLabel,
        'ORDER#'
      );

      setText(
        reference,
        data.order_number ||
        '—'
      );

    }


    const valueMap = [

      [
        '[data-ztrk-result-carrier]',
        data.carrier
      ],

      [
        '[data-ztrk-result-tracking]',
        data.tracking_number
      ],

      [
        '[data-ztrk-result-status]',
        data.status_label
      ],

      [
        '[data-ztrk-result-latest]',
        data.latest_update
      ],

      [
        '[data-ztrk-result-estimate]',
        data.estimated_delivery
      ],

      [
        '[data-ztrk-result-message]',
        data.message
      ]

    ];


    valueMap.forEach(
      ([selector, value]) => {

        setText(
          root.querySelector(selector),
          value || '—'
        );

      }
    );


    updateProgress(
      root,
      data.progress_index
    );


    const demoBadge =
      root.querySelector(
        SELECTORS.demoBadge
      );


    if (demoBadge) {

      demoBadge.hidden =
        !data.demo;

    }


    setState(
      root,
      'success'
    );


    announce(
      root,
      data.demo
        ? 'Demo tracking result displayed.'
        : 'Tracking information loaded.'
    );

  }


  /* ========================================================
     SAFE THEME-EDITOR DEMO

     Synthetic only.
     Never claims to be a real shipment.
     ======================================================== */

  function createDemoResult(
    mode,
    values
  ) {

    return {

      demo: true,

      verified: true,

      order_number:
        mode === 'order'
          ? values.orderNumber
          : 'DEMO ORDER',

      carrier:
        'Demo Courier',

      tracking_number:
        mode === 'tracking'
          ? values.trackingNumber
          : 'DEMO123456789',

      status_label:
        'In Transit',

      message:
        'Your Zebaesh parcel is on its way.',

      latest_update:
        'Demo shipment event',

      estimated_delivery:
        'Demo Preview',

      progress_index:
        2

    };

  }


  /* ========================================================
     LIVE SECURE REQUEST

     API credentials never exist in browser code.
     ======================================================== */

  async function requestTracking(
    payload
  ) {

    if (!ZTRK_CONFIG.live) {

      throw new Error(
        'TRACKING_NOT_CONNECTED'
      );

    }


    const controller =
      new AbortController();


    const timeout =
      window.setTimeout(
        () => controller.abort(),
        ZTRK_CONFIG.requestTimeout
      );


    try {

      const response =
        await fetch(
          ZTRK_CONFIG.endpoint,
          {
            method:
              'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            credentials:
              'same-origin',

            cache:
              'no-store',

            redirect:
              'error',

            body:
              JSON.stringify(payload),

            signal:
              controller.signal
          }
        );


      if (!response.ok) {

        throw new Error(
          'TRACKING_REQUEST_FAILED'
        );

      }


      const data =
        await response.json();


      if (
        !data ||
        typeof data !== 'object'
      ) {

        throw new Error(
          'INVALID_TRACKING_RESPONSE'
        );

      }


      return data;

    } finally {

      window.clearTimeout(
        timeout
      );

    }

  }


  /* ========================================================
     RESET
     ======================================================== */

  function resetTracker(root) {

    setState(
      root,
      'idle'
    );


    const mode =
      root.dataset.lookupMode ||
      'order';


    setLookupMode(
      root,
      mode
    );


    const activePanel =
      root.querySelector(
        `[data-ztrk-mode-panel="${mode}"]`
      );


    const firstInput =
      activePanel
        ? activePanel.querySelector(
            'input:not([disabled])'
          )
        : null;


    if (firstInput) {

      window.setTimeout(
        () => firstInput.focus(),
        50
      );

    }

  }


  /* ========================================================
     INITIALIZE TRACKER
     ======================================================== */

  function initTracker(root) {

    if (
      root.dataset.ztrkInitialized ===
      'true'
    ) {
      return;
    }


    root.dataset.ztrkInitialized =
      'true';


    const form =
      root.querySelector(
        SELECTORS.form
      );


    if (!form) return;


    const orderInput =
      root.querySelector(
        SELECTORS.orderInput
      );


    const identityInput =
      root.querySelector(
        SELECTORS.identityInput
      );


    const trackingInput =
      root.querySelector(
        SELECTORS.trackingInput
      );


    const submitButton =
      root.querySelector(
        SELECTORS.submit
      );


    let submitting =
      false;


    let lastAttempt =
      0;


    /* ---------------------------------------------
       MODE TABS
       --------------------------------------------- */

    root
      .querySelectorAll(
        SELECTORS.modeButton
      )
      .forEach((button) => {

        button.addEventListener(
          'click',
          () => {

            if (submitting) return;

            setLookupMode(
              root,
              button.dataset.ztrkModeButton
            );

          }
        );

      });


    setLookupMode(
      root,
      'order'
    );


    /* ---------------------------------------------
       CLEAR FIELD ERRORS
       --------------------------------------------- */

    [
      orderInput,
      identityInput,
      trackingInput
    ]
      .filter(Boolean)
      .forEach((input) => {

        input.addEventListener(
          'input',
          () => {
            clearFieldError(input);
          }
        );

      });


    /* ---------------------------------------------
       RESET BUTTONS
       --------------------------------------------- */

    root
      .querySelectorAll(
        SELECTORS.reset
      )
      .forEach((button) => {

        button.addEventListener(
          'click',
          () => {

            if (submitting) return;

            resetTracker(root);

          }
        );

      });


    /* ---------------------------------------------
       SUBMIT
       --------------------------------------------- */

    form.addEventListener(
      'submit',
      async (event) => {

        event.preventDefault();


        if (submitting) return;


        const now =
          Date.now();


        if (
          now - lastAttempt <
          ZTRK_CONFIG.clientCooldown
        ) {
          return;
        }


        const mode =
          root.dataset.lookupMode ||
          'order';


        const orderNumber =
          normalizeOrderNumber(
            orderInput
              ? orderInput.value
              : ''
          );


        const identity =
          normalizeIdentity(
            identityInput
              ? identityInput.value
              : ''
          );


        const trackingNumber =
          normalizeTrackingNumber(
            trackingInput
              ? trackingInput.value
              : ''
          );


        let valid = true;


        [
          orderInput,
          identityInput,
          trackingInput
        ]
          .filter(Boolean)
          .forEach(
            clearFieldError
          );


        /* ORDER MODE */

        if (mode === 'order') {

          if (
            !validateOrderNumber(
              orderNumber
            )
          ) {

            showFieldError(
              orderInput,
              'Please enter a valid order number.'
            );

            valid = false;

          }


          if (
            !validateIdentity(
              identity
            )
          ) {

            showFieldError(
              identityInput,
              'Please enter the email or phone used at checkout.'
            );

            valid = false;

          }

        }


        /* TRACKING MODE */

        if (mode === 'tracking') {

          if (
            !validateTrackingNumber(
              trackingNumber
            )
          ) {

            showFieldError(
              trackingInput,
              'Please enter a valid tracking number.'
            );

            valid = false;

          }

        }


        if (!valid) {

          const firstInvalid =
            form.querySelector(
              '[aria-invalid="true"]'
            );


          if (firstInvalid) {
            firstInvalid.focus();
          }


          return;

        }


        lastAttempt =
          now;


        submitting =
          true;


        setSubmitting(
          submitButton,
          true
        );


        setState(
          root,
          'loading'
        );


        announce(
          root,
          'Checking delivery information.'
        );


        try {

          /* =======================================
             THEME EDITOR DEMO
             ======================================= */

          if (canUseDemo()) {

            await wait(
              ZTRK_CONFIG.demoDelay
            );


            const demo =
              createDemoResult(
                mode,
                {
                  orderNumber,
                  trackingNumber
                }
              );


            renderTrackingResult(
              root,
              demo,
              mode
            );


            return;

          }


          /* =======================================
             API NOT CONNECTED
             ======================================= */

          if (!ZTRK_CONFIG.live) {

            await wait(
              450
            );


            setState(
              root,
              'unavailable'
            );


            announce(
              root,
              'Live tracking is not connected yet.'
            );


            return;

          }


          /* =======================================
             FUTURE LIVE REQUEST
             ======================================= */

          const payload =
            mode === 'tracking'
              ? {
                  mode:
                    'tracking',

                  tracking_number:
                    trackingNumber
                }
              : {
                  mode:
                    'order',

                  order_number:
                    orderNumber,

                  identity:
                    identity
                };


          const data =
            await requestTracking(
              payload
            );


          /*
            Generic verification response.
            Do not reveal whether a specific order exists.
          */

          if (
            data.verified ===
            false
          ) {

            setState(
              root,
              'error'
            );


            announce(
              root,
              'We could not verify these details.'
            );


            return;

          }


          renderTrackingResult(
            root,
            data,
            mode
          );


        } catch (error) {

          /*
            Internal/API details are deliberately not exposed
            to the customer.
          */

          console.warn(
            '[Zebaesh Tracking] Tracking request unavailable.'
          );


          setState(
            root,
            'unavailable'
          );


          announce(
            root,
            'Tracking is temporarily unavailable.'
          );

        } finally {

          setSubmitting(
            submitButton,
            false
          );


          submitting =
            false;

        }

      }
    );

  }


  /* ========================================================
     INITIALIZE
     ======================================================== */

  function initAllTrackers() {

    document
      .querySelectorAll(
        SELECTORS.root
      )
      .forEach(
        initTracker
      );

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initAllTrackers,
      {
        once: true
      }
    );

  } else {

    initAllTrackers();

  }


  /* Shopify Theme Editor support */

  document.addEventListener(
    'shopify:section:load',
    initAllTrackers
  );

})();